import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import FadeInSection from './FadeInSection';
import ValuationTool from './ValuationTool';
import '../styles/WestwoodHighlands.scss';

// Unlisted, co-branded (Lily Lai × Lena Koenig / SERHANT) market-snapshot +
// valuation page for the Westwood Highlands open house. Reached only via QR /
// direct link — noindex, not in nav, not in sitemap.

// Westwood Highlands market snapshot — single-family, District 4, last six months
// (Feb–Aug 2026). Source: Westwood_Highlands_Market_Snapshot one-sheet, data
// through Aug 18, 2026. Update these together if a new snapshot is produced.
const SNAPSHOT = [
  { value: '13', label: 'Homes Sold', note: 'Closed 2/18–8/18/26 · 1 active' },
  { value: '$2.58M', label: 'Median Sold Price', note: 'Avg $2.91M · $2.15M–$4.80M' },
  { value: '$1,073', label: 'Median $ / Sq Ft', note: 'Avg $1,077' },
  { value: '12', label: 'Median Days on Market', note: 'Avg 20 · 0.3 mo supply' },
];

const SECONDARY = [
  { value: '119%', label: 'Sold / List Price' },
  { value: '0.3', label: 'Months of Supply' },
  { value: '1', label: 'Active Listing' },
];

// 13 closed sales, 2/18/26–8/18/26. `top` marks the period's highest sale.
const SOLD = [
  { date: '02/26/26', address: '199 Brentwood Ave', bedbath: '4 / 5', sqft: '3,568', price: '$4,800,000', ppsf: '$1,345', dom: '6', top: true },
  { date: '03/06/26', address: '815 Joost Ave', bedbath: '4 / 4', sqft: '2,520', price: '$3,000,000', ppsf: '$1,190', dom: '20' },
  { date: '03/09/26', address: '591 Los Palmos Dr', bedbath: '4 / 2', sqft: '2,004', price: '$2,150,000', ppsf: '$1,073', dom: '20' },
  { date: '03/23/26', address: '23 Cresta Vista Dr', bedbath: '3 / 3', sqft: '2,620', price: '$2,580,000', ppsf: '$985', dom: '11' },
  { date: '03/25/26', address: '345 Hazelwood Ave', bedbath: '3 / 4', sqft: '2,280', price: '$2,920,000', ppsf: '$1,281', dom: '13' },
  { date: '03/31/26', address: '72 Cresta Vista Dr', bedbath: '4 / 3', sqft: '2,950', price: '$2,535,000', ppsf: '$859', dom: '11' },
  { date: '04/15/26', address: '930 Monterey Blvd', bedbath: '3 / 2', sqft: '1,793', price: '$2,200,000', ppsf: '$1,227', dom: '4' },
  { date: '06/01/26', address: '335 Yerba Buena Ave', bedbath: '3 / 2', sqft: '1,885', price: '$2,520,000', ppsf: '$1,337', dom: '13' },
  { date: '06/26/26', address: '248 Hazelwood Ave', bedbath: '4 / 3', sqft: '2,424', price: '$2,300,000', ppsf: '$949', dom: '—' },
  { date: '07/02/26', address: '221 Cresta Vista Dr', bedbath: '4 / 5', sqft: '3,300', price: '$2,250,000', ppsf: '$682', dom: '119' },
  { date: '07/09/26', address: '210 Lansdale Ave', bedbath: '5 / 5', sqft: '5,354', price: '$4,625,000', ppsf: '$864', dom: '8' },
  { date: '07/22/26', address: '96 Brentwood Ave', bedbath: '3 / 2', sqft: '2,695', price: '$3,299,892', ppsf: '$1,224', dom: '15' },
  { date: '08/14/26', address: '411 Hazelwood Ave', bedbath: '3 / 2', sqft: '2,748', price: '$2,700,000', ppsf: '$983', dom: '5' },
];

const INTRO = {
  eyebrow: 'Westwood Highlands · Instant Estimate',
  title: 'What Is Your Westwood Highlands Home Worth?',
  subheading:
    "Enter your details below for a personalized valuation based on recent comparable sales right here in the neighborhood.",
};

function WestwoodHighlands() {
  // This page hides the site chrome, so the Navbar's useLockBodyScroll side effect
  // (which normally overrides the global `body { overflow-x: hidden }`) never runs.
  // Without it, `overflow-x: hidden` turns <body> into a scroll container with a
  // tiny scroll range, so the page scroll gets stuck. Flag the body so we can
  // switch to `overflow-x: clip` (clips horizontally without a scroll container).
  useEffect(() => {
    document.body.classList.add('wh-scroll-fix');
    return () => document.body.classList.remove('wh-scroll-fix');
  }, []);

  return (
    <>
      <Helmet>
        <title>680 Los Palmos | Lily Lai × Lena Koenig</title>
        {/* Unlisted: keep it out of search results. */}
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="A Westwood Highlands market snapshot and instant home valuation, presented by Lily Lai and Lena Koenig of SERHANT." />
      </Helmet>

      <div className="wh-page">
        {/* ── Co-branded header ── */}
        <FadeInSection>
          <header className="wh-cobrand">
            <p className="wh-cobrand-eyebrow">A Neighborhood Market Snapshot, Presented Together</p>
            <div className="wh-cobrand-pair">
              <div className="wh-party">
                <span className="wh-logo-slot">
                  <img className="wh-logo-lily" src="/lily-city-real-estate-white.png" alt="Lily · City Real Estate" />
                </span>
                <span className="wh-party-name">Lily Lai</span>
              </div>
              <span className="wh-cobrand-x">×</span>
              <div className="wh-party">
                <span className="wh-logo-slot">
                  <img className="wh-logo-serhant" src="/serhant-banner.png" alt="SERHANT" />
                </span>
                <span className="wh-party-name">Lena Koenig</span>
              </div>
            </div>
          </header>
        </FadeInSection>

        {/* ── Valuation tool (shared engine) — the primary action ── */}
        <ValuationTool source="680-los-palmos" intro={INTRO} />

        {/* ── Market snapshot band ── */}
        <FadeInSection>
          <section className="wh-snapshot">
            <div className="wh-snapshot-head">
              <p className="wh-snapshot-eyebrow">The Market Behind Your Estimate · SF District 4</p>
              <h2 className="wh-snapshot-title">Westwood Highlands Market Snapshot</h2>
              <p className="wh-snapshot-sub">Last six months, February–August 2026 · data through Aug 18, 2026</p>
            </div>
            <div className="wh-snapshot-grid">
              {SNAPSHOT.map((s) => (
                <div className="wh-stat" key={s.label}>
                  <span className="wh-stat-value">{s.value}</span>
                  <span className="wh-stat-label">{s.label}</span>
                  {s.note && <span className="wh-stat-note">{s.note}</span>}
                </div>
              ))}
            </div>
            <div className="wh-secondary">
              {SECONDARY.map((s) => (
                <div className="wh-secondary-item" key={s.label}>
                  <span className="wh-secondary-value">{s.value}</span>
                  <span className="wh-secondary-label">{s.label}</span>
                </div>
              ))}
            </div>
          </section>
        </FadeInSection>

        {/* ── Recently sold table ── */}
        <FadeInSection>
          <section className="wh-sold">
            <div className="wh-sold-head">
              <h2 className="wh-sold-title">Recently Sold in Westwood Highlands</h2>
              <p className="wh-sold-sub">13 closed sales · 2/18/26 – 8/18/26</p>
            </div>
            <div className="wh-table-wrap">
              <table className="wh-table">
                <thead>
                  <tr>
                    <th>Sold</th>
                    <th>Address</th>
                    <th>Beds / Baths</th>
                    <th>Sq Ft</th>
                    <th>Sold Price</th>
                    <th>$ / Sq Ft</th>
                    <th>DOM</th>
                  </tr>
                </thead>
                <tbody>
                  {SOLD.map((r) => (
                    <tr key={r.address} className={r.top ? 'wh-row-top' : ''}>
                      <td>{r.date}</td>
                      <td className="wh-td-address">{r.address}{r.top && <span className="wh-top-tag">Top sale</span>}</td>
                      <td>{r.bedbath}</td>
                      <td>{r.sqft}</td>
                      <td className="wh-td-price">{r.price}</td>
                      <td>{r.ppsf}</td>
                      <td>{r.dom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="wh-sold-note">Highlighted: 199 Brentwood Ave, the period's top sale. DOM unavailable for the public-record sale at 248 Hazelwood Ave.</p>
          </section>
        </FadeInSection>

        {/* ── Co-branded contact footer ── */}
        <FadeInSection>
          <footer className="wh-contact">
            <p className="wh-contact-lead">Presented together by</p>
            <div className="wh-contact-grid">
              <div className="wh-contact-card">
                <p className="wh-contact-name">Lily Lai</p>
                <p className="wh-contact-role">Realtor® · The Lurie Group · City Real Estate</p>
                <p className="wh-contact-line"><a href="tel:4157282623">415 728 2623</a> · <a href="mailto:lily@lilylaisf.com">lily@lilylaisf.com</a></p>
                <p className="wh-contact-line"><a href="https://www.lilylaisf.com" target="_blank" rel="noreferrer">lilylaisf.com</a> · DRE #02123349</p>
                <p className="wh-contact-langs">English · 中文 · 廣東話 / 普通話</p>
              </div>
              <div className="wh-contact-card">
                <p className="wh-contact-name">Lena Koenig</p>
                <p className="wh-contact-role">Realtor® · LK Homes · SERHANT.</p>
                <p className="wh-contact-line"><a href="tel:4159710252">415.971.0252</a> · <a href="mailto:Lena@Serhant.com">Lena@Serhant.com</a></p>
                <p className="wh-contact-line"><a href="https://www.lenakhomes.com" target="_blank" rel="noreferrer">lenakhomes.com</a> · DRE #02248437</p>
              </div>
            </div>
          </footer>
        </FadeInSection>
      </div>
    </>
  );
}

export default WestwoodHighlands;
